import { useContext } from 'react';
import { Box, Divider, Grid, Typography } from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppSelector } from '@/redux/hooks';
import { VersionGroupedByParent } from '@/redux/slices/slice_types';
import { selectVersionKeys } from '@/redux/selectors/orderPlacementSelectors';
import { isTitleEntityV2 } from '@/_utils/utils';
import { TitleVersionChip } from '@/exports/columnCommonComponents';

const marketingItems = [
  'adspots',
  'ad_spots',
  'clips',
  'dailies',
  'epkbroll',
  'epk/broll',
  'featurette',
  'graphics',
  'promo',
  'sizzle reel',
  'sizzlereel',
  'testing',
  'trailer'
];

interface OPVersionSelectionsListProps {
  selectedVersions: VersionGroupedByParent;
}

const OPVersionSelectionsList = ({ selectedVersions }: OPVersionSelectionsListProps) => {
  // Safety check: if selectedVersions contains schema-like data, return null
  if (selectedVersions && typeof selectedVersions === 'object') {
    const keys = Object.keys(selectedVersions);
    const hasSchemaKeys = keys.some((key) => ['$ref', 'type', 'properties', 'items', 'default'].includes(key));
    if (hasSchemaKeys) {
      console.warn('OPVersionSelectionsList received schema data instead of version data:', selectedVersions);
      return null;
    }
  }

  const baseChildKeys = Object.keys(selectedVersions);
  const versionsByKey = useAppSelector(selectVersionKeys);
  const customTitleDisplay = useContext(ConfigContext)?.orderPlacementConfig?.selectedVersionTitleDisplay;

  const selectTitleById = (id: string) => {
    const titleKey = String(id).substring(1);
    return versionsByKey[+titleKey] || {};
  };

  const ChildEntity = ({ parent }: { parent: VersionGroupedByParent }) => {
    const parentKeys = Object.keys(parent).sort((a, b) => {
      return Number(selectTitleById(a).sequence_order) - Number(selectTitleById(b).sequence_order);
    });

    return (
      <Box>
        {parentKeys.map((parentKey) => {
          const child = selectTitleById(parentKey);
          const isChildV2 = isTitleEntityV2(child);
          const children = isChildV2 ? child.relationships.children : child?.children;

          // Check if this is a direct version object (like {_732897: {version object}})
          const content = parent[parentKey];
          if (content?.entity_type === 'version') {
            // This is a direct version, render it as a simple version chip
            const version = selectTitleById(parentKey);
            const isVersionV2 = isTitleEntityV2(version);
            const versionParent = selectTitleById(`_${isVersionV2 ? version?.relationships.parent?.id : version.parent}`);
            const isFeatureContent = version.lineage_meta?.parent_entity_type === 'feature';

            return (
              <Box key={parentKey}>
                {customTitleDisplay ? (
                  customTitleDisplay(!marketingItems.includes(versionParent?.entity_type), child, version, parentKey)
                ) : (
                  <Grid container alignItems="center" sx={{ py: 0.5 }}>
                    <Grid item maxWidth="50%">
                      <Typography sx={{ wordBreak: 'break-all' }}>
                        {!isFeatureContent && !marketingItems.includes(versionParent?.entity_type) && (
                          <span style={{ opacity: 0.6, paddingRight: 5 }}>
                            E{!isNaN(Number(child.sequence_order)) ? Number(child.sequence_order) : '?'}:
                          </span>
                        )}
                        {child.name}
                      </Typography>
                    </Grid>
                    <Grid item flex="auto">
                      <Divider sx={{ borderColor: 'action.selected', borderStyle: 'dashed', mx: 2 }} />
                    </Grid>
                    <Grid item>
                      <TitleVersionChip version={version} size="small" withTooltip />
                    </Grid>
                  </Grid>
                )}
              </Box>
            );
          }

          let hasOnlyVersionChildren = false;
          if (!children || !children.length) {
            hasOnlyVersionChildren = ['episode', ...marketingItems].includes(child?.entity_type);
          } else {
            hasOnlyVersionChildren = Boolean(children?.length && children?.every((c: { entity_type: string }) => c.entity_type === 'version'));
          }

          const mb = baseChildKeys.includes(parentKey) ? 2 : 0; // margin bottom
          if (hasOnlyVersionChildren) {
            return (
              <Box key={parentKey} sx={{ mb }}>
                {(() => {
                  // Check if parent[parentKey] is a direct version object OR if it contains only one version
                  const content = parent[parentKey];

                  // Case 1: Direct version object
                  if (content?.entity_type === 'version') {
                    const version = content as any;
                    const versionParent = selectTitleById(`_${version.parent}`);
                    const isFeatureContent = version.lineage_meta?.parent_entity_type === 'feature';

                    return (
                      <Box key={parentKey}>
                        {customTitleDisplay ? (
                          customTitleDisplay(!marketingItems.includes(versionParent?.entity_type), child, version, parentKey)
                        ) : (
                          <Grid container alignItems="center" sx={{ py: 0.5 }}>
                            <Grid item maxWidth="50%">
                              <Typography sx={{ wordBreak: 'break-all' }}>
                                {!isFeatureContent && !marketingItems.includes(versionParent?.entity_type) && (
                                  <span style={{ opacity: 0.6, paddingRight: 5 }}>
                                    E{!isNaN(Number(child.sequence_order)) ? Number(child.sequence_order) : '?'}:
                                  </span>
                                )}
                                {child.name}
                              </Typography>
                            </Grid>
                            <Grid item flex="auto">
                              <Divider sx={{ borderColor: 'action.selected', borderStyle: 'dashed', mx: 2 }} />
                            </Grid>
                            <Grid item>
                              <TitleVersionChip version={version} size="small" withTooltip />
                            </Grid>
                          </Grid>
                        )}
                      </Box>
                    );
                  }

                  // Case 2: Single version in nested structure (like {_732897: {version object}})
                  const versionKeys = Object.keys(content || {}).filter((key) => key.startsWith('_'));

                  if (versionKeys.length === 1) {
                    const singleVersionKey = versionKeys[0];
                    const version = selectTitleById(singleVersionKey);
                    const isVersionV2 = isTitleEntityV2(version);
                    const versionParent = selectTitleById(`_${isVersionV2 ? version?.relationships.parent?.id : version.parent}`);
                    const isFeatureContent = version.lineage_meta?.parent_entity_type === 'feature';

                    return (
                      <Box key={parentKey}>
                        {customTitleDisplay ? (
                          customTitleDisplay(!marketingItems.includes(versionParent?.entity_type), child, version, singleVersionKey)
                        ) : (
                          <Grid container alignItems="center" sx={{ py: 0.5 }}>
                            <Grid item maxWidth="50%">
                              <Typography sx={{ wordBreak: 'break-all' }}>
                                {!isFeatureContent && !marketingItems.includes(versionParent?.entity_type) && (
                                  <span style={{ opacity: 0.6, paddingRight: 5 }}>
                                    E{!isNaN(Number(child.sequence_order)) ? Number(child.sequence_order) : '?'}:
                                  </span>
                                )}
                                {child.name}
                              </Typography>
                            </Grid>
                            <Grid item flex="auto">
                              <Divider sx={{ borderColor: 'action.selected', borderStyle: 'dashed', mx: 2 }} />
                            </Grid>
                            <Grid item>
                              <TitleVersionChip version={version} size="small" withTooltip />
                            </Grid>
                          </Grid>
                        )}
                      </Box>
                    );
                  }

                  // Case 3: Handle nested structure (original logic)
                  const filteredKeys = Object.keys(content || {}).filter((key) => {
                    // Only process version keys (start with _) and exclude metadata/schema properties
                    return key.startsWith('_') && !['metadata_schema', 'metadata_languages', 'lineage_meta'].includes(key);
                  });

                  return filteredKeys
                    .sort((a, b) => selectTitleById(a).name?.localeCompare(selectTitleById(b).name ?? '') ?? 0)
                    .map((versionKey) => {
                      const version = selectTitleById(versionKey);
                      const isVersionV2 = isTitleEntityV2(version);
                      const versionParent = selectTitleById(`_${isVersionV2 ? version?.relationships.parent?.id : version.parent}`);
                      return (
                        <Box key={versionKey}>
                          {customTitleDisplay ? (
                            customTitleDisplay(!marketingItems.includes(versionParent.entity_type), child, version, versionKey)
                          ) : (
                            <Grid container key={versionKey} alignItems="center" sx={{ py: 0.5 }}>
                              <Grid item maxWidth="50%">
                                <Typography sx={{ wordBreak: 'break-all' }}>
                                  {!marketingItems.includes(versionParent.entity_type) && (
                                    <span style={{ opacity: 0.6, paddingRight: 5 }}>
                                      E{!isNaN(Number(child.sequence_order)) ? Number(child.sequence_order) : '?'}:
                                    </span>
                                  )}
                                  {child.name}
                                </Typography>
                              </Grid>
                              <Grid item flex="auto">
                                <Divider sx={{ borderColor: 'action.selected', borderStyle: 'dashed', mx: 2 }} />
                              </Grid>
                              <Grid item>
                                <TitleVersionChip key={versionKey} version={version} size="small" withTooltip />
                              </Grid>
                            </Grid>
                          )}
                        </Box>
                      );
                    });
                })()}
              </Box>
            );
          }

          if (typeof parent[parentKey] === 'string') return;

          return (
            <Box key={parentKey} sx={{ mb }}>
              <Typography fontWeight="bold">{child.name}</Typography>
              {parent[parentKey] && <ChildEntity key={parentKey} parent={parent[parentKey]} />}
            </Box>
          );
        })}
      </Box>
    );
  };

  return <ChildEntity parent={selectedVersions} />;
};

export default OPVersionSelectionsList;
